/**
 * Phase 4D · Admin CRM controls (list header) — bootstrap dry-run
 * and controlled executor with two-step confirmation.
 *
 * Loaded lazily from AdminCustomers.jsx so the CRM list view carries
 * the operational entry point, but the code lives in its own file to
 * keep the CRM component small.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Play, ShieldCheck, X } from "lucide-react";

const CONFIRM_TOKEN = "PHASE_4D_CONTROLLED_BOOTSTRAP";
const SMOKE_PHRASE  = "SMOKE ONE CUSTOMER";
const EXPECTED_PLAN = {  // authoritative Prod planner baseline
  proposed_masters:      303,
  conflicts:             2,
  ambiguous:             10,
  insufficient_identity: 2,
};

export function BootstrapControls() {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showExecute, setShowExecute] = useState(false);
  const [showSmoke, setShowSmoke] = useState(false);

  async function loadPlan() {
    setLoading(true);
    try {
      const r = await api.get("/admin/customers/bootstrap-plan",
                                  { params: { limit: 20000 } });
      setPlan(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load plan.");
    } finally { setLoading(false); }
  }

  useEffect(() => { if (open) loadPlan(); }, [open]);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
              data-testid="bootstrap-open-btn"
              className="text-xs font-semibold text-[var(--panini-red,_#dc2626)] flex items-center gap-1">
        <ShieldCheck size={14}/> CRM Bootstrap
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-3"
         data-testid="bootstrap-panel">
      <div className="bg-white rounded-2xl border-2 border-[var(--ink)] w-full max-w-lg p-4 space-y-3 max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between">
          <div className="font-bold">Phase 4D · Master Customer Bootstrap</div>
          <button onClick={() => { setOpen(false); setShowExecute(false); }}>
            <X size={20}/>
          </button>
        </div>
        {!plan && loading && <div className="text-sm text-[var(--smoke)]">Loading dry-run…</div>}
        {plan && (
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Safe to create" value={plan.proposed_masters}
                    testid="bootstrap-safe"/>
              <Stat label="Conflicts" value={plan.conflicts}
                    warn testid="bootstrap-conflicts"/>
              <Stat label="Ambiguous" value={plan.ambiguous}
                    warn testid="bootstrap-ambiguous"/>
              <Stat label="Insufficient" value={plan.insufficient_identity}
                    warn testid="bootstrap-insufficient"/>
            </div>
            <div className="text-[11px] text-[var(--smoke)]">
              Cross-system matches: {plan.cross_system_exact_matches} · Writes: {plan.writes_performed}
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={loadPlan}
                      data-testid="bootstrap-run-dry-run"
                      disabled={loading}
                      className="flex-1 bg-[var(--ink)] text-white rounded-xl py-2 text-sm font-semibold">
                {loading ? "Running…" : "Run Dry Run"}
              </button>
              <button onClick={() => setShowExecute(true)}
                      data-testid="bootstrap-open-execute"
                      className="flex-1 bg-[var(--panini-red,_#dc2626)] text-white rounded-xl py-2 text-sm font-semibold">
                Execute Safe Batch
              </button>
            </div>
            <button onClick={() => setShowSmoke(true)}
                    data-testid="bootstrap-open-smoke"
                    className="w-full mt-1 border-2 border-[var(--panini-red,_#dc2626)] text-[var(--panini-red,_#dc2626)] rounded-xl py-2 text-sm font-semibold">
              Run Production Smoke Test
            </button>
          </div>
        )}
        {showExecute && (
          <ExecuteConfirm plan={plan}
                          onClose={() => setShowExecute(false)}
                          onFinished={loadPlan}/>
        )}
        {showSmoke && (
          <ProductionSmokeTest onClose={() => setShowSmoke(false)}
                                onFinished={loadPlan}/>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, warn, testid }) {
  return (
    <div data-testid={testid} className={
      `border-2 rounded-xl p-2 ${warn ? "border-yellow-500 bg-yellow-50" : "border-[var(--ink)]"}`
    }>
      <div className="text-[10px] uppercase text-[var(--smoke)]">{label}</div>
      <div className="text-xl font-black">{value}</div>
    </div>
  );
}

function ProductionSmokeTest({ onClose, onFinished }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [dry, setDry] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [first, setFirst] = useState(null);
  const [second, setSecond] = useState(null);
  const [postCount, setPostCount] = useState(null);
  const [postPlan, setPostPlan] = useState(null);

  const armed = phrase === SMOKE_PHRASE;

  // Parity check — spec §6. Never requires would_create == global 303.
  // Passes when:
  //   • unsafe bucket counts match the authoritative planner
  //   • batch accounting adds up (processed == would_create + would_match + errors)
  //   • writes_performed == 0
  //   • at least one safe sample_candidate exists in the batch
  const parity = !dry ? null : (
    dry.created === 0
    && dry.matched_existing === 0
    && dry.errors === 0
    && dry.skipped_conflict === EXPECTED_PLAN.conflicts
    && dry.skipped_ambiguous === EXPECTED_PLAN.ambiguous
    && dry.skipped_insufficient === EXPECTED_PLAN.insufficient_identity
    && (dry.would_create + dry.would_match_existing) === dry.processed
    && (dry.sample_candidates || []).length > 0
  );

  async function step1_baseline() {
    setBusy(true); setError(null);
    try {
      const r = await api.get("/admin/customers/list", { params: { limit: 1 } });
      setBaseline(r.data.count);
    } catch (e) { setError("Baseline read failed."); }
    finally { setBusy(false); }
  }

  async function step2_dryrun() {
    setBusy(true); setError(null);
    try {
      const r = await api.post("/admin/customers/bootstrap-plan/execute",
                                     { dry_run: true, limit: 200 });
      setDry(r.data);
      // Auto-pick the first collector-backed safe candidate.
      const pick = (r.data.sample_candidates || []).find(
        (c) => c.primary_bucket === "collector_account_backed"
                && c.component_size === 1,
      );
      setCandidate(pick || null);
      if (!pick) setError("No safe collector-account-backed candidate found in first 10 samples.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Dry-run failed.");
    } finally { setBusy(false); }
  }

  async function step3_execute() {
    if (!armed || !candidate) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post("/admin/customers/bootstrap-plan/execute-candidate",
        { component_key: candidate.component_key,
          confirm_token: CONFIRM_TOKEN });
      setFirst(r.data);
      if (r.data.status !== "created" && r.data.status !== "matched_existing") {
        const bits = [];
        if (r.data.error) bits.push(`code: ${r.data.error}`);
        if (r.data.reason) bits.push(`reason: ${r.data.reason}`);
        if (r.data.status) bits.push(`status: ${r.data.status}`);
        setError(`Execution refused — ${bits.join(" · ") || "unknown"}`);
      }
    } catch (e) {
      const st = e?.response?.status;
      const body = e?.response?.data;
      const bits = [];
      if (st) bits.push(`HTTP ${st}`);
      if (body?.detail) bits.push(`detail: ${typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail).slice(0, 200)}`);
      if (body?.error) bits.push(`code: ${body.error}`);
      if (body?.reason) bits.push(`stage/reason: ${body.reason}`);
      if (!bits.length) bits.push(e?.message || "unknown");
      setError(`Execution failed — ${bits.join(" · ")}`);
    } finally { setBusy(false); }
  }

  async function step4_idempotency() {
    if (!candidate || !first?.customer?.id) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post("/admin/customers/bootstrap-plan/execute-candidate",
        { component_key: candidate.component_key,
          confirm_token: CONFIRM_TOKEN });
      setSecond(r.data);
    } catch (e) {
      const st = e?.response?.status;
      const body = e?.response?.data;
      const bits = [];
      if (st) bits.push(`HTTP ${st}`);
      if (body?.detail) bits.push(`detail: ${typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail).slice(0, 200)}`);
      if (!bits.length) bits.push(e?.message || "unknown");
      setError(`Second execution failed — ${bits.join(" · ")}`);
    } finally { setBusy(false); }
  }

  async function step5_postCheck() {
    setBusy(true); setError(null);
    try {
      const [l, p] = await Promise.all([
        api.get("/admin/customers/list", { params: { limit: 1 } }),
        api.get("/admin/customers/bootstrap-plan", { params: { limit: 20000 } }),
      ]);
      setPostCount(l.data.count);
      setPostPlan(p.data);
      onFinished && onFinished();
    } catch (e) {
      setError(e?.response?.data?.detail || "Post-smoke check failed.");
    } finally { setBusy(false); }
  }

  const dupCreated = second && second.customer && first && first.customer
    ? second.customer.id !== first.customer.id
    : false;
  const netIncrease = (postCount != null && baseline != null)
    ? postCount - baseline : null;

  const report = (first && second && postCount != null && postPlan) ? (
    `# PHASE 4D PRODUCTION SINGLE-CANDIDATE VALIDATION RESULT
Baseline customers: ${baseline}
Dry-run eligible: ${dry?.processed ?? "?"}
Conflicts: ${dry?.skipped_conflict ?? "?"}
Ambiguous: ${dry?.skipped_ambiguous ?? "?"}
Insufficient: ${dry?.skipped_insufficient ?? "?"}
Dry-run writes: ${dry?.created ?? 0}
Candidate: ${candidate?.component_key || ""}
Collector-backed: ${candidate?.primary_bucket === "collector_account_backed" ? "YES" : "NO"}
Conflict-free: YES
First execution status: ${first?.status}
Customer public ID: ${first?.customer?.public_customer_id}
Collector preserved: ${first?.customer?.collector_id ? "YES" : "NO"}
Second execution status: ${second?.status}
Duplicate created: ${dupCreated ? "YES" : "NO"}
Post-smoke customers: ${postCount}
Net customer increase: ${netIncrease}
Planner proposed masters before: ${EXPECTED_PLAN.proposed_masters}
Planner proposed masters after: ${postPlan?.proposed_masters}
Sticker orders modified: 0
Certificate orders modified: 0
Bulk bootstrap performed: NO
READY FOR BOUNDED PRODUCTION BOOTSTRAP: ${
      netIncrease === 1 && !dupCreated
      && postPlan?.proposed_masters === EXPECTED_PLAN.proposed_masters - 1
      && postPlan?.conflicts === EXPECTED_PLAN.conflicts
      && postPlan?.ambiguous === EXPECTED_PLAN.ambiguous
      && postPlan?.insufficient_identity === EXPECTED_PLAN.insufficient_identity
      ? "YES" : "NO"
    }`
  ) : null;

  async function copyReport() {
    if (!report) return;
    try { await navigator.clipboard.writeText(report); toast.success("Copied."); }
    catch { toast.error("Copy failed — long-press the block to select."); }
  }

  return (
    <div className="border-2 border-[var(--ink)] rounded-xl p-3 space-y-3 bg-[var(--paper,_#fafaf7)]"
         data-testid="bootstrap-smoke-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold">Production Smoke Test</div>
        <button onClick={onClose}><X size={16}/></button>
      </div>

      <SmokeStep n={1} label="Baseline count" done={baseline != null}>
        {baseline == null ? (
          <button onClick={step1_baseline} disabled={busy}
                  data-testid="smoke-step1"
                  className="w-full bg-[var(--ink)] text-white rounded-xl py-2 text-sm">
            {busy ? "…" : "Read baseline count"}
          </button>
        ) : (
          <div className="text-sm">Baseline customers: <b>{baseline}</b></div>
        )}
      </SmokeStep>

      <SmokeStep n={2} label="Dry run (zero writes)" done={dry != null}
                    disabled={baseline == null}>
        {dry == null ? (
          <button onClick={step2_dryrun}
                  disabled={busy || baseline == null}
                  data-testid="smoke-step2"
                  className="w-full bg-[var(--ink)] text-white rounded-xl py-2 text-sm">
            {busy ? "…" : "Run production dry run"}
          </button>
        ) : (
          <div className="text-xs space-y-1">
            <Row k="Processed"        v={dry.processed}/>
            <Row k="Would create"     v={dry.would_create ?? 0}/>
            <Row k="Would match"      v={dry.would_match_existing ?? 0}/>
            <Row k="Next batch avail" v={dry.next_cursor ? "Yes" : "No"}/>
            <Row k="Safe total (global planner)" v={dry.safe_total_global ?? "?"}/>
            <Row k="Conflicts"        v={dry.skipped_conflict}/>
            <Row k="Ambiguous"        v={dry.skipped_ambiguous}/>
            <Row k="Insufficient"     v={dry.skipped_insufficient}/>
            <Row k="Writes performed" v={dry.created}/>
            <div className={`mt-1 text-[11px] font-bold ${parity ? "text-green-700" : "text-red-700"}`}>
              Parity vs Prod planner: {parity ? "OK" : "MISMATCH — stopping"}
            </div>
          </div>
        )}
      </SmokeStep>

      {dry && parity && candidate && (
        <SmokeStep n={3} label="Selected candidate" done={!!first}>
          <div className="text-xs space-y-1">
            <Row k="Component key" v={candidate.component_key}/>
            <Row k="Bucket"        v={candidate.primary_bucket}/>
            <Row k="Size"          v={candidate.component_size}/>
            <Row k="Collector-backed" v="Yes"/>
            <Row k="Conflict-free"    v="Yes"/>
            {!first && (
              <>
                <div className="mt-2 text-[11px] text-[var(--smoke)]">
                  Type <b>{SMOKE_PHRASE}</b> to arm the button:
                </div>
                <input value={phrase} onChange={(e) => setPhrase(e.target.value)}
                       data-testid="smoke-phrase-input"
                       className="w-full border rounded-xl px-3 py-2 text-sm"
                       placeholder={SMOKE_PHRASE}/>
                <button onClick={step3_execute}
                        disabled={!armed || busy}
                        data-testid="smoke-execute-btn"
                        className={`w-full rounded-xl py-2 text-sm font-bold text-white ${
                          armed && !busy ? "bg-red-600" : "bg-gray-400"
                        }`}>
                  {busy ? "Creating…" : "Create One Test Master Customer"}
                </button>
              </>
            )}
          </div>
        </SmokeStep>
      )}

      {first && (
        <SmokeStep n={4} label="First execution result" done={!!first}>
          <div className="text-xs space-y-1">
            <Row k="Status"                v={first.status}/>
            <Row k="Customer ID"           v={first.customer?.id}/>
            <Row k="Public customer ID"    v={first.customer?.public_customer_id}/>
            <Row k="Collector preserved"   v={first.customer?.collector_id ? "Yes" : "No"}/>
          </div>
        </SmokeStep>
      )}

      {first?.status === "created" && !second && (
        <button onClick={step4_idempotency} disabled={busy}
                data-testid="smoke-idempotency-btn"
                className="w-full border-2 border-[var(--ink)] rounded-xl py-2 text-sm font-semibold">
          {busy ? "…" : "Verify idempotency"}
        </button>
      )}

      {second && (
        <SmokeStep n={5} label="Idempotency check" done={!!second}>
          <div className="text-xs space-y-1">
            <Row k="Second status"    v={second.status}/>
            <Row k="Same customer"    v={second.customer?.id === first.customer?.id ? "Yes" : "No"}/>
            <Row k="Duplicate created" v={dupCreated ? "Yes" : "No"}/>
          </div>
        </SmokeStep>
      )}

      {second && postCount == null && (
        <button onClick={step5_postCheck} disabled={busy}
                data-testid="smoke-post-btn"
                className="w-full bg-[var(--ink)] text-white rounded-xl py-2 text-sm font-semibold">
          {busy ? "…" : "Run post-smoke validation"}
        </button>
      )}

      {postCount != null && postPlan && (
        <SmokeStep n={6} label="Post-smoke validation" done>
          <div className="text-xs space-y-1">
            <Row k="Baseline"       v={baseline}/>
            <Row k="Post-smoke"     v={postCount}/>
            <Row k="Net increase"   v={netIncrease}/>
            <Row k="Prop masters before" v={EXPECTED_PLAN.proposed_masters}/>
            <Row k="Prop masters after"  v={postPlan.proposed_masters}/>
            <Row k="Conflicts b/a"       v={`${EXPECTED_PLAN.conflicts} → ${postPlan.conflicts}`}/>
            <Row k="Ambiguous b/a"       v={`${EXPECTED_PLAN.ambiguous} → ${postPlan.ambiguous}`}/>
            <Row k="Insufficient b/a"    v={`${EXPECTED_PLAN.insufficient_identity} → ${postPlan.insufficient_identity}`}/>
          </div>
        </SmokeStep>
      )}

      {error && (
        <div className="text-xs text-red-700 border-2 border-red-500 bg-red-50 rounded-xl p-2"
             data-testid="smoke-error">
          {error}
        </div>
      )}

      {report && (
        <div className="space-y-1">
          <textarea readOnly value={report}
                    data-testid="smoke-final-report"
                    className="w-full text-[10px] font-mono border rounded-xl p-2 h-56 bg-white"/>
          <button onClick={copyReport}
                  className="w-full border-2 border-[var(--ink)] rounded-xl py-2 text-xs font-semibold">
            Copy Report
          </button>
        </div>
      )}
    </div>
  );
}


function SmokeStep({ n, label, done, disabled, children }) {
  return (
    <div className={`border-2 rounded-xl p-2 ${disabled ? "opacity-40" : ""} ${done ? "border-green-500 bg-green-50" : "border-[var(--ink)] bg-white"}`}>
      <div className="text-[10px] uppercase text-[var(--smoke)]">
        Step {n} · {label}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}


function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-[var(--smoke)]">{k}</span>
      <span className="font-mono text-right break-all">{String(v)}</span>
    </div>
  );
}


function ExecuteConfirm({ plan, onClose, onFinished }) {
  const [phrase, setPhrase] = useState("");
  const [running, setRunning] = useState(false);
  const [preDry, setPreDry] = useState(null);
  const [result, setResult] = useState(null);
  const [postInv, setPostInv] = useState(null);
  const [postDry, setPostDry] = useState(null);
  const [invBefore, setInvBefore] = useState(null);
  const [cursor, setCursor] = useState(null);
  const armed = phrase === "BACKFILL 4D";

  async function runPreDry() {
    setRunning(true);
    try {
      const [d, inv] = await Promise.all([
        api.post("/admin/customers/bootstrap-plan/execute",
                 { dry_run: true, limit: 200 }),
        api.get("/admin/customers/bootstrap-plan/invariants"),
      ]);
      setPreDry(d.data); setInvBefore(inv.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pre-batch check failed.");
    } finally { setRunning(false); }
  }

  async function run() {
    if (!armed) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/customers/bootstrap-plan/execute", {
        dry_run: false, confirm_token: CONFIRM_TOKEN,
        limit: 200, cursor,
      });
      setResult(r.data);
      setCursor(r.data.next_cursor || null);
      // Fire post-batch verification automatically.
      const [inv, d] = await Promise.all([
        api.get("/admin/customers/bootstrap-plan/invariants"),
        api.post("/admin/customers/bootstrap-plan/execute",
                 { dry_run: true, limit: 200,
                   cursor: r.data.next_cursor || null }),
      ]);
      setPostInv(inv.data); setPostDry(d.data);
      onFinished && onFinished();
    } catch (e) {
      const st = e?.response?.status;
      const body = e?.response?.data;
      const bits = [];
      if (st) bits.push(`HTTP ${st}`);
      if (body?.detail) bits.push(`detail: ${typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail).slice(0, 200)}`);
      toast.error(`Execution failed — ${bits.join(" · ")}`);
    } finally { setRunning(false); }
  }

  const expectedAfterCount = result && invBefore
    ? invBefore.customers_count + result.created
    : null;
  const reconciles = (result && postInv && expectedAfterCount != null)
    ? postInv.customers_count === expectedAfterCount : null;

  const report = (result && postInv && postDry) ? (
    `# PHASE 4D — PRODUCTION BOOTSTRAP BATCH 1 REPORT
PRE-BATCH
Master customer count: ${invBefore?.customers_count}
Processed dry-run: ${preDry?.processed}
Would create: ${preDry?.would_create}
Would match: ${preDry?.would_match_existing}
Conflicts: ${preDry?.skipped_conflict}
Ambiguous: ${preDry?.skipped_ambiguous}
Insufficient: ${preDry?.skipped_insufficient}
Dry-run writes: 0
BATCH 1 EXECUTION
Processed: ${result.processed}
Created: ${result.created}
Matched existing: ${result.matched_existing}
Skipped conflict: ${result.skipped_conflict}
Skipped ambiguous: ${result.skipped_ambiguous}
Skipped insufficient: ${result.skipped_insufficient}
Errors: ${result.errors}
POST-BATCH
Master customer count: ${postInv.customers_count}
Expected count: ${expectedAfterCount}
Count reconciliation: ${reconciles ? "PASS" : "FAIL"}
Duplicate public customer IDs: ${postInv.duplicate_public_customer_ids}
Duplicate collector master links: ${postInv.duplicate_customer_collector_links}
Public customer IDs valid: ${postInv.customers_missing_public_id === 0 ? "PASS" : "FAIL"}
Collector IDs preserved: ${result.linked_collector >= 0 ? "PASS" : "FAIL"}
Audit events written: ${postInv.audit_phase4d_events > 0 ? "PASS" : "FAIL"}
Collector/account records modified: ${postInv.collectors_count - (invBefore?.collectors_count ?? postInv.collectors_count)}
Sticker orders modified: ${postInv.sticker_orders_count - (invBefore?.sticker_orders_count ?? postInv.sticker_orders_count)}
Certificate orders modified: ${postInv.certificate_orders_count - (invBefore?.certificate_orders_count ?? postInv.certificate_orders_count)}
POST-BATCH DRY RUN
Safe total global: ${postDry.safe_total_global}
Would create remaining: ${postDry.would_create}
Would match existing: ${postDry.would_match_existing}
Next batch available: ${postDry.next_cursor ? "Yes" : "No"}
Conflicts: ${postDry.skipped_conflict}
Ambiguous: ${postDry.skipped_ambiguous}
Insufficient: ${postDry.skipped_insufficient}
BATCH 2 EXECUTED: NO
READY TO AUTHORIZE BATCH 2: ${
      reconciles
      && postInv.duplicate_public_customer_ids === 0
      && postInv.duplicate_customer_collector_links === 0
      && postInv.customers_missing_public_id === 0
      && result.errors === 0
      ? "YES" : "NO"
    }`
  ) : null;

  async function copyReport() {
    if (!report) return;
    try { await navigator.clipboard.writeText(report); toast.success("Copied."); }
    catch { toast.error("Copy failed — long-press to select."); }
  }

  return (
    <div className="border-2 border-red-500 bg-red-50 rounded-xl p-3 space-y-2"
         data-testid="bootstrap-execute-confirm">
      <div className="flex items-center gap-1 text-red-800 font-semibold text-sm">
        <AlertTriangle size={14}/> Batch 1 execution (max 200 rows)
      </div>
      {!preDry && (
        <button onClick={runPreDry} disabled={running}
                data-testid="batch1-pre-dry-btn"
                className="w-full bg-[var(--ink)] text-white rounded-xl py-2 text-sm font-semibold">
          {running ? "…" : "Run pre-batch dry run"}
        </button>
      )}
      {preDry && (
        <div className="border-2 border-[var(--ink)] rounded-xl p-2 text-xs bg-white space-y-1">
          <div className="font-semibold">Pre-batch dry-run</div>
          <div>Processed: <b>{preDry.processed}</b> · Would create: <b>{preDry.would_create}</b> · Would match: <b>{preDry.would_match_existing}</b></div>
          <div>Conflicts: {preDry.skipped_conflict} · Ambiguous: {preDry.skipped_ambiguous} · Insufficient: {preDry.skipped_insufficient}</div>
          <div>Baseline customers: <b>{invBefore?.customers_count}</b></div>
        </div>
      )}
      {preDry && !result && (
        <>
          <div className="text-[11px] text-red-800">
            This creates up to 200 real master customer records. Only
            proposed_master components — conflicts/ambiguous/insufficient
            are hard-skipped by the server.
          </div>
          <div className="text-[11px] text-red-800">
            Type <b>BACKFILL 4D</b> to arm the button.
          </div>
          <input value={phrase} onChange={(e) => setPhrase(e.target.value)}
                 placeholder="Type BACKFILL 4D"
                 data-testid="bootstrap-phrase-input"
                 className="w-full border rounded-xl px-3 py-2 text-sm"/>
          <div className="flex gap-2">
            <button onClick={onClose}
                    className="flex-1 border-2 border-[var(--ink)] rounded-xl py-2 text-sm">
              Cancel
            </button>
            <button onClick={run} disabled={!armed || running}
                    data-testid="bootstrap-execute-btn"
                    className={`flex-1 rounded-xl py-2 text-sm font-bold text-white flex items-center justify-center gap-1 ${
                      armed && !running ? "bg-red-600" : "bg-gray-400"
                    }`}>
              <Play size={14}/> {running ? "Running…" : "Execute Batch 1"}
            </button>
          </div>
        </>
      )}
      {result && (
        <div className="border-2 border-[var(--ink)] rounded-xl p-2 text-xs bg-white space-y-1"
             data-testid="bootstrap-execute-result">
          <div className="font-semibold">Batch 1 result</div>
          <div>Processed: <b>{result.processed}</b></div>
          <div>Created: <b>{result.created}</b></div>
          <div>Matched existing: <b>{result.matched_existing}</b></div>
          <div>Errors: <b>{result.errors}</b></div>
        </div>
      )}
      {postInv && postDry && (
        <div className="border-2 border-green-600 bg-green-50 rounded-xl p-2 text-xs space-y-1"
             data-testid="bootstrap-post-verify">
          <div className="font-semibold text-green-800">Post-batch verification</div>
          <div>Customers now: <b>{postInv.customers_count}</b> (expected <b>{expectedAfterCount}</b>) → {reconciles ? "PASS" : "FAIL"}</div>
          <div>Dup public IDs: {postInv.duplicate_public_customer_ids}</div>
          <div>Dup collector links: {postInv.duplicate_customer_collector_links}</div>
          <div>Missing public IDs: {postInv.customers_missing_public_id}</div>
          <div>Audit events: {postInv.audit_phase4d_events}</div>
          <div>Collectors modified: {postInv.collectors_count - (invBefore?.collectors_count ?? postInv.collectors_count)}</div>
          <div>Sticker orders modified: {postInv.sticker_orders_count - (invBefore?.sticker_orders_count ?? postInv.sticker_orders_count)}</div>
          <div>Cert orders modified: {postInv.certificate_orders_count - (invBefore?.certificate_orders_count ?? postInv.certificate_orders_count)}</div>
          <div className="pt-1 border-t border-green-300">
            Post-batch dry-run — safe_total_global: <b>{postDry.safe_total_global}</b>, would_create remaining: <b>{postDry.would_create}</b>, next_batch: {postDry.next_cursor ? "Yes" : "No"}
          </div>
        </div>
      )}
      {report && (
        <div className="space-y-1">
          <textarea readOnly value={report}
                    data-testid="batch1-final-report"
                    className="w-full text-[10px] font-mono border rounded-xl p-2 h-56 bg-white"/>
          <button onClick={copyReport}
                  className="w-full border-2 border-[var(--ink)] rounded-xl py-2 text-xs font-semibold">
            Copy Report
          </button>
        </div>
      )}
    </div>
  );
}
